import React from 'react'
import { useFormik } from 'formik'
import { Col, Form, Input, Modal, Row } from 'antd'
import { NumberFormatValues, NumericFormat } from 'react-number-format'
import { IconCancel } from 'components/icons'
import Card from 'components/card/Card'
import Button from 'components/button/Button'
import ErrorHintForm from 'components/errors/ErrorHintForm'
import RequiredMark from 'components/form/RequiredMark'
import {
  AdminsEcommerceCreateProductCategoryRequest,
  AdminsEcommerceUpdateProductCategoryRequest,
  UserRoleEnum,
} from 'generated/api'
import { validationSchemaFormCategory } from 'helpers/validation'
import { classNameInput } from 'helpers/formik/classNameForm'

export interface IProps {
  isOpen: boolean
  isLoading: boolean
  categoryId: string
  initForm:
    | AdminsEcommerceCreateProductCategoryRequest
    | AdminsEcommerceUpdateProductCategoryRequest
  onCancel: () => void
  onSubmit: (
    values:
      | AdminsEcommerceCreateProductCategoryRequest
      | AdminsEcommerceUpdateProductCategoryRequest,
  ) => void
}

const ModalFormCatgories = (props: IProps) => {
  const { isOpen, isLoading, categoryId, initForm, onCancel, onSubmit } = props

  const formik = useFormik({
    initialValues: initForm,
    enableReinitialize: true,
    validationSchema: validationSchemaFormCategory,
    validateOnBlur: true,
    onSubmit: async values => {
      onSubmit(values)
    },
  })

  const { values, errors, touched, setFieldValue, handleSubmit } = formik

  const classNameInputName = classNameInput('name', formik)

  const title = categoryId ? 'Ubah Kategori' : 'Tambah Kategori'

  const handleUpdateRolePointUsageItems = (role: UserRoleEnum, val: NumberFormatValues) => {
    const updatedRolePointUsageItems = values.rolePointUsageItems.map(item =>
      item.role === role ? { ...item, maxPointUsage: val.floatValue ?? '' } : item,
    )
    setFieldValue('rolePointUsageItems', updatedRolePointUsageItems)
  }

  const valueMaxPointUsageByRole = (role: UserRoleEnum) => {
    return values.rolePointUsageItems.find(item => item.role === role)?.maxPointUsage
  }

  const errorFieldMaxPointUsageByRole = (role: UserRoleEnum) => {
    const index = values.rolePointUsageItems.findIndex(item => item.role === role)
    const errorItem = errors.rolePointUsageItems?.[index]
    const touchedItem = touched.rolePointUsageItems?.[index]

    if (
      typeof errorItem !== 'string' &&
      typeof errorItem === 'object' &&
      errorItem !== null &&
      'maxPointUsage' in errorItem &&
      touchedItem?.maxPointUsage
    ) {
      return errorItem.maxPointUsage as string
    }

    return ''
  }

  return (
    <Modal
      data-testid="modal-form-categories"
      title={title}
      className="modal-admin"
      onCancel={onCancel}
      open={isOpen}
      footer={null}
      width={607}
      centered
      closeIcon={<IconCancel strokeWidth="2" color="black" />}
    >
      <Card
        loading={isLoading}
        dataTestId="card_category_form"
        spinnerTestId="loader_category_form"
        className="rounded-xl p-0"
      >
        <Form className="form" layout="vertical" preserve={false} requiredMark={RequiredMark}>
          <Form.Item
            name="name"
            label="Nama Kategori"
            className="font-bold mb-1"
            required
            valuePropName="name"
          >
            <Input
              name="name"
              data-testid="input-name"
              placeholder="Masukkan nama kategori"
              onChange={({ target: { value } }) => setFieldValue('name', value)}
              className={`w-full ${classNameInputName}`}
              value={values.name}
              status={errors.name && touched.name ? 'error' : ''}
            />
          </Form.Item>
          {errors.name && touched.name ? (
            <ErrorHintForm errorMessage={errors.name} dataTestId="error-message-name" />
          ) : null}
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                name="maxPointUsage"
                label="% Point Semua"
                className="font-bold mb-1"
                required
                valuePropName="maxpointusage"
              >
                <NumericFormat
                  allowNegative={false}
                  className={classNameInput('maxPointUsage', formik)}
                  customInput={Input}
                  data-testid="input-maxPointUsage-all"
                  decimalScale={0}
                  decimalSeparator=","
                  onValueChange={val => setFieldValue('maxPointUsage', val.floatValue ?? '')}
                  placeholder="0"
                  status={errors.maxPointUsage && touched.maxPointUsage ? 'error' : ''}
                  thousandSeparator="."
                  value={values.maxPointUsage}
                />
              </Form.Item>
              {errors.maxPointUsage && touched.maxPointUsage ? (
                <ErrorHintForm
                  errorMessage={errors.maxPointUsage}
                  dataTestId="error-message-maxPointUsage"
                />
              ) : null}
            </Col>
            <Col span={12}>
              <Form.Item
                name="rolePointUsageItems[0].maxPointUsage"
                label="% Point Agen"
                className="font-bold mb-1"
                required
                valuePropName="rolepointusageitems"
              >
                <NumericFormat
                  allowNegative={false}
                  className={classNameInput('rolePointUsageItems[0].maxPointUsage', formik)}
                  customInput={Input}
                  data-testid="input-maxPointUsage-agent"
                  decimalScale={0}
                  decimalSeparator=","
                  onValueChange={val => handleUpdateRolePointUsageItems(UserRoleEnum.Agent, val)}
                  placeholder="0"
                  status={errorFieldMaxPointUsageByRole(UserRoleEnum.Agent) ? 'error' : ''}
                  thousandSeparator="."
                  value={valueMaxPointUsageByRole(UserRoleEnum.Agent)}
                />
              </Form.Item>
              {errorFieldMaxPointUsageByRole(UserRoleEnum.Agent) ? (
                <ErrorHintForm
                  errorMessage={errorFieldMaxPointUsageByRole(UserRoleEnum.Agent)}
                  dataTestId="error-message-maxPointUsage-agent"
                />
              ) : null}
            </Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                name="rolePointUsageItems[1].maxPointUsage"
                label="% Point Kordinator Lapangan"
                className="font-bold mb-1"
                required
                valuePropName="rolepointusageitems"
              >
                <NumericFormat
                  allowNegative={false}
                  className={classNameInput('rolePointUsageItems[1].maxPointUsage', formik)}
                  customInput={Input}
                  data-testid="input-maxPointUsage-fieldCoordinator"
                  decimalScale={0}
                  decimalSeparator=","
                  onValueChange={val =>
                    handleUpdateRolePointUsageItems(UserRoleEnum.FieldCoordinator, val)
                  }
                  placeholder="0"
                  status={
                    errorFieldMaxPointUsageByRole(UserRoleEnum.FieldCoordinator) ? 'error' : ''
                  }
                  thousandSeparator="."
                  value={valueMaxPointUsageByRole(UserRoleEnum.FieldCoordinator)}
                />
              </Form.Item>
              {errorFieldMaxPointUsageByRole(UserRoleEnum.FieldCoordinator) ? (
                <ErrorHintForm
                  errorMessage={errorFieldMaxPointUsageByRole(UserRoleEnum.FieldCoordinator)}
                  dataTestId="error-message-maxPointUsage-fieldCoordinator"
                />
              ) : null}
            </Col>
            <Col span={12}>
              <Form.Item
                name="rolePointUsageItems[2].maxPointUsage"
                label="% Point Supir"
                className="font-bold mb-1"
                required
                valuePropName="rolepointusageitems"
              >
                <NumericFormat
                  allowNegative={false}
                  className={classNameInput('rolePointUsageItems[2].maxPointUsage', formik)}
                  customInput={Input}
                  data-testid="input-maxPointUsage-driver"
                  decimalScale={0}
                  decimalSeparator=","
                  onValueChange={val => handleUpdateRolePointUsageItems(UserRoleEnum.Driver, val)}
                  placeholder="0"
                  status={errorFieldMaxPointUsageByRole(UserRoleEnum.Driver) ? 'error' : ''}
                  thousandSeparator="."
                  value={valueMaxPointUsageByRole(UserRoleEnum.Driver)}
                />
              </Form.Item>
              {errorFieldMaxPointUsageByRole(UserRoleEnum.Driver) ? (
                <ErrorHintForm
                  errorMessage={errorFieldMaxPointUsageByRole(UserRoleEnum.Driver)}
                  dataTestId="error-message-maxPointUsage-driver"
                />
              ) : null}
            </Col>
          </Row>
          <Row gutter={8} className="mb-4">
            <Col span={12}>
              <Form.Item
                name="rolePointUsageItems[3].maxPointUsage"
                label="% Point Petani"
                className="font-bold mb-1"
                required
                valuePropName="rolepointusageitems"
              >
                <NumericFormat
                  allowNegative={false}
                  className={classNameInput('rolePointUsageItems[3].maxPointUsage', formik)}
                  customInput={Input}
                  data-testid="input-maxPointUsage-farmer"
                  decimalScale={0}
                  decimalSeparator=","
                  onValueChange={val => handleUpdateRolePointUsageItems(UserRoleEnum.Farmer, val)}
                  placeholder="0"
                  status={errorFieldMaxPointUsageByRole(UserRoleEnum.Farmer) ? 'error' : ''}
                  thousandSeparator="."
                  value={valueMaxPointUsageByRole(UserRoleEnum.Farmer)}
                />
              </Form.Item>
              {errorFieldMaxPointUsageByRole(UserRoleEnum.Farmer) ? (
                <ErrorHintForm
                  errorMessage={errorFieldMaxPointUsageByRole(UserRoleEnum.Farmer)}
                  dataTestId="error-message-maxPointUsage-farmer"
                />
              ) : null}
            </Col>
            <Col span={12}>
              <Form.Item
                name="rolePointUsageItems[4].maxPointUsage"
                label="% Point Pemanen"
                className="font-bold mb-1"
                required
                valuePropName="rolepointusageitems"
              >
                <NumericFormat
                  allowNegative={false}
                  className={classNameInput('rolePointUsageItems[4].maxPointUsage', formik)}
                  customInput={Input}
                  data-testid="input-maxPointUsage-harvester"
                  decimalScale={0}
                  decimalSeparator=","
                  onValueChange={val =>
                    handleUpdateRolePointUsageItems(UserRoleEnum.Harvester, val)
                  }
                  placeholder="0"
                  status={errorFieldMaxPointUsageByRole(UserRoleEnum.Harvester) ? 'error' : ''}
                  thousandSeparator="."
                  value={valueMaxPointUsageByRole(UserRoleEnum.Harvester)}
                />
              </Form.Item>
              {errorFieldMaxPointUsageByRole(UserRoleEnum.Harvester) ? (
                <ErrorHintForm
                  errorMessage={errorFieldMaxPointUsageByRole(UserRoleEnum.Harvester)}
                  dataTestId="error-message-maxPointUsage-harvester"
                />
              ) : null}
            </Col>
          </Row>
          <Button
            type="submit"
            loading={isLoading}
            block={true}
            onClick={handleSubmit}
            data-testid="button-submit-form-category"
            className="pt-3 rounded-xl pb-3 font-bold text-sm"
            iconPosition="right"
            label="SIMPAN"
            variant="solid-primary"
          />
        </Form>
      </Card>
    </Modal>
  )
}

export default ModalFormCatgories
